export type RecordType =
  | 'individual_achievement'
  | 'individual_memory'
  | 'individual_skill'
  | 'individual_legacy'
  | 'institution_verified_record'
  | 'institution_certificate'
  | 'heritage_archive'
  | 'cultural_collection'
  | 'public_proof_card';

export type PillarType =
  | 'Achievement'
  | 'Performance'
  | 'Hero'
  | 'Mischief'
  | 'Favorites'
  | 'History'
  | 'Skill / Legacy';

export type VerificationStatus =
  | 'Draft'
  | 'Submitted'
  | 'Peer Reviewed'
  | 'Witness Confirmed'
  | 'Institution Verified'
  | 'Gold Verified'
  | 'Public Record'
  | 'Archived';

export type VisibilityType =
  | 'private'
  | 'community_public'
  | 'institution_public'
  | 'external_link'
  | 'verified_public_proof'
  | 'community_summary'
  | 'restricted_proof'
  | 'share_link'
  | 'external_public';

export type TrustLevelField =
  | 'draft'
  | 'self_recorded'
  | 'evidence_added'
  | 'witness_confirmed'
  | 'institution_reviewed'
  | 'verified_proof'
  | 'certificate_eligible'
  | 'certificate_issued'
  | 'archived';

export type ClaimType =
  | 'personal_memory'
  | 'community_story'
  | 'achievement'
  | 'contribution'
  | 'heritage'
  | 'degree'
  | 'employment'
  | 'license'
  | 'award'
  | 'volunteer'
  | 'program_completion'
  | 'institutional_recognition'
  | 'public_service'
  | 'other';

export type PublicRecord = {
  id: string;
  title: string;
  description: string;
  recordType: RecordType;
  ownerType: 'individual' | 'institution';
  ownerName: string;
  ownerAvatar?: string;
  ownerSubtitle?: string;
  pillar: PillarType;
  verificationStatus: VerificationStatus;
  visibility: VisibilityType;
  externalSharingEnabled: boolean;
  date: string;
  year: number;
  locationName: string;
  latitude?: number;
  longitude?: number;
  imageUrl?: string;
  evidenceCount: number;
  /** Public proof card reference (default structured share layer). */
  proofCardId?: string;
  /** Set only when a certificate has been issued — not automatic per record. */
  certificateId?: string;
  /** Owner opt-in for certificate issuance; false for stories/memories by default. */
  allowCertificateIssuance?: boolean;
  /** Explicit trust ladder level (computed when omitted). */
  trustLevel?: TrustLevelField;
  claimType?: ClaimType;
  evidenceStatus?: 'none' | 'added' | 'reviewed';
  witnessStatus?: 'none' | 'pending' | 'confirmed';
  institutionReviewStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  certificateEligible?: boolean;
  certificateIssued?: boolean;
  /** Certificates are private by default. */
  certificateShareEnabled?: boolean;
  certificateVisibility?: 'private' | 'share_link' | 'external_public';
  communitySummaryEnabled?: boolean;
  externalShareToken?: string;
  externalShareExpiresAt?: string;
  evidencePrivate?: boolean;
  reviewerNotesPrivate?: boolean;
  tags: string[];
  isArchived: boolean;
  isFeatured?: boolean;
  /** When false, record stays off Geospatial Map even if community-public. */
  showOnGeospatialMap?: boolean;
  /** Curator / review approval for public map discovery (required for display when set). */
  approvedForPublicMap?: boolean;
  /** Live heritage_archive_items id — opens /heritage/entry/[id] instead of static drawer. */
  heritageLiveId?: string;
};

export const PILLAR_TOKENS: Record<PillarType, { bg: string; text: string; icon: string }> = {
  History: { bg: '#faeeda', text: '#854f0b', icon: '#ef9f27' },
  Achievement: { bg: '#eaf3de', text: '#3b6d11', icon: '#97c459' },
  Performance: { bg: '#e6f1fb', text: '#185fa5', icon: '#85b7eb' },
  Hero: { bg: '#fcebeb', text: '#a32d2d', icon: '#f09595' },
  Mischief: { bg: '#eeedfe', text: '#534ab7', icon: '#afa9ec' },
  Favorites: { bg: '#fbeaf0', text: '#993556', icon: '#ed93b1' },
  'Skill / Legacy': { bg: '#e1f5ee', text: '#0f6e56', icon: '#5dcaa5' },
};

export const STATUS_TOKENS: Record<
  VerificationStatus,
  { color: string; bg: string; label: string }
> = {
  Draft: { color: '#6b7280', bg: 'rgba(107,114,128,0.12)', label: 'Draft' },
  Submitted: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Submitted' },
  'Peer Reviewed': { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', label: 'Peer Reviewed' },
  'Witness Confirmed': {
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.12)',
    label: 'Witness Confirmed',
  },
  'Institution Verified': {
    color: '#34d399',
    bg: 'rgba(52,211,153,0.12)',
    label: 'Institution Verified',
  },
  'Gold Verified': { color: '#e9a84c', bg: 'rgba(233,168,76,0.14)', label: 'Gold Verified' },
  'Public Record': { color: '#22d3ee', bg: 'rgba(34,211,238,0.12)', label: 'Public Record' },
  Archived: { color: '#8b9cb0', bg: 'rgba(139,156,176,0.12)', label: 'Archived' },
};

export const VISIBILITY_TOKENS: Record<
  VisibilityType,
  { color: string; bg: string; label: string }
> = {
  private: { color: '#6b7280', bg: 'rgba(107,114,128,0.1)', label: 'Private' },
  community_public: { color: '#22d3ee', bg: 'rgba(34,211,238,0.1)', label: 'Community Public' },
  institution_public: {
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.1)',
    label: 'Institution Public',
  },
  external_link: { color: '#34d399', bg: 'rgba(52,211,153,0.1)', label: 'External Link' },
  verified_public_proof: {
    color: '#e9a84c',
    bg: 'rgba(233,168,76,0.1)',
    label: 'Verified Public Proof',
  },
  community_summary: { color: '#22d3ee', bg: 'rgba(34,211,238,0.1)', label: 'Community Summary' },
  restricted_proof: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Restricted Proof' },
  share_link: { color: '#34d399', bg: 'rgba(52,211,153,0.1)', label: 'Share Link' },
  external_public: { color: '#e9a84c', bg: 'rgba(233,168,76,0.1)', label: 'External Public' },
};

const VERIFICATION_STATUS_KEY_IDS: Record<VerificationStatus, string> = {
  Draft: 'draft',
  Submitted: 'submitted',
  'Peer Reviewed': 'peer_reviewed',
  'Witness Confirmed': 'witness_confirmed',
  'Institution Verified': 'institution_verified',
  'Gold Verified': 'gold_verified',
  'Public Record': 'public_record',
  Archived: 'archived',
};

const VISIBILITY_KEY_IDS: Record<VisibilityType, string> = {
  private: 'private',
  community_public: 'community_public',
  institution_public: 'institution_public',
  external_link: 'external_link',
  verified_public_proof: 'verified_public_proof',
  community_summary: 'community_summary',
  restricted_proof: 'restricted_proof',
  share_link: 'share_link',
  external_public: 'external_public',
};

/**
 * Keys into the `verification_status`/`visibility_status` i18n namespaces for a status's
 * localized label. These files have no React context to call useTranslation() themselves, so
 * callers pass these into their own t() -- with STATUS_TOKENS/VISIBILITY_TOKENS' `label` above
 * as the English fallback -- rather than ever rendering the hardcoded English fields directly.
 */
export function verificationStatusLabelKey(status: VerificationStatus): string {
  return `verification_status.${VERIFICATION_STATUS_KEY_IDS[status]}`;
}

export function visibilityLabelKey(visibility: VisibilityType): string {
  return `visibility_status.${VISIBILITY_KEY_IDS[visibility]}`;
}
